<template>
    <div id="administration-orders">
        <transition name="fade" mode="out-in">
            <div key="loading" v-if="!orders">
                <APILoading :font-size="30" :text="$t('AdministrationOrders.loading')"/>
            </div>
            <div key="orders" v-else class="row">
                <div class="col-12">
                    <v-client-table id="orders" :data="displayedOrders" :columns="columns" :options="options">
                        <button v-if="isAdministrationOrdersBlockageEnabled" slot="afterFilter" class="btn"
                                :class="showOnlyPrepressBlockedOrders ? 'btn-warning' : 'btn-outline-warning'"
                                @click="togglePrepressBlockedOrders">
                            <i class="fa fa-filter mr-2"/>
                            <span v-html="prepressBlockedFilterLabel"/>
                        </button>
                        <SubmitBtn slot="afterFilter" :loading="isLoadingCSV.quote" classes="btn btn-info" :text="$t('AdministrationOrders.exportQuotes')"
                                   @click="downloadCSV('quote')"/>
                        <SubmitBtn slot="afterFilter" :loading="isLoadingCSV.order" classes="btn btn-info" :text="$t('AdministrationOrders.exportOrders')"
                                   @click="downloadCSV('order')"/>
                        <a slot="reference" slot-scope="props" href="#" class="text-medium" v-html="`#${props.row.reference}`"
                           @click.prevent="showProfileOrdersDetailsModal(props.row)"/>
                        <div slot="client" slot-scope="props" v-html="displayedOrderUser(props.row)"/>
                        <div slot="createdAt" slot-scope="props" v-html="properDate(props.row.createdAt)"/>
                        <div v-if="props.row.type === 'order'" slot="sentAt" slot-scope="props" v-html="displayedSendingDate(props.row)"/>
                        <OrderProductionStatusLabel v-if="props.row.type === 'order'" slot="status" slot-scope="props" :order="props.row" class="text-center"/>
                        <OrderPaymentStatusLabel v-if="props.row.type === 'order'" slot="payment" slot-scope="props" :order="props.row" class="text-center"/>
                        <div slot="prepress" slot-scope="props" class="prepress-cell">
                            <div v-if="props.row.type !== 'order'">-</div>
                            <div v-else-if="!isOrderTrackedForPrepress(props.row)">-</div>
                            <div v-else-if="hasPrepressBlockedProducts(props.row)">
                                <div class="badge badge-danger p-2 mb-2">
                                    <i class="fa fa-exclamation-triangle mr-1"/>
                                    <span v-html="$t('AdministrationOrders.prepressBlocked')"/>
                                    <span class="ml-1" v-html="`(${getPrepressBlockedProducts(props.row).length})`"/>
                                </div>
                                <div v-for="(product, index) in getPrepressBlockedProducts(props.row)" :key="`${props.row._id}-prepress-product-${index}`"
                                     class="prepress-product">
                                    <div class="prepress-product-header">
                                        <router-link class="text-less-bold prepress-product-link" target="_blank" :to="`/product/${product._id}`">
                                            {{ product.title || `#${product._id}` }}
                                        </router-link>
                                        <button type="button" class="btn btn-info btn-xs" @click.prevent="showAdministrationProductModal(product)">
                                            <i class="fa fa-pen mr-1"/>
                                            <span>Modifier le produit</span>
                                        </button>
                                    </div>
                                    <div class="prepress-product-statuses">
                                        <span class="badge mr-1 mb-1"
                                              :class="prepressStatusClass(getProductFileStatus(product, 'corpus'))">
                                            <span v-html="$t('ProductFilesStatusBadge.corpus')"/>
                                            :
                                            <span v-html="$t(`ProductFilesStatusBadge.${prepressStatusLabelKey(getProductFileStatus(product, 'corpus'))}`)"/>
                                        </span>
                                        <span class="badge mr-1 mb-1"
                                              :class="prepressStatusClass(getProductFileStatus(product, 'cover'))">
                                            <span v-html="$t('ProductFilesStatusBadge.cover')"/>
                                            :
                                            <span v-html="$t(`ProductFilesStatusBadge.${prepressStatusLabelKey(getProductFileStatus(product, 'cover'))}`)"/>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div v-else-if="hasClientPendingProducts(props.row)">
                                <div class="badge badge-danger p-2 mb-2">
                                    <i class="fa fa-user-clock mr-1"/>
                                    <span v-html="$t('AdministrationOrders.clientBlocked')"/>
                                    <span class="ml-1" v-html="`(${getClientPendingProducts(props.row).length})`"/>
                                </div>
                                <div v-for="(product, index) in getClientPendingProducts(props.row)" :key="`${props.row._id}-client-product-${index}`"
                                     class="prepress-product">
                                    <div class="prepress-product-header">
                                        <router-link class="text-less-bold prepress-product-link" target="_blank" :to="`/product/${product._id}`">
                                            {{ product.title || `#${product._id}` }}
                                        </router-link>
                                        <button type="button" class="btn btn-info btn-xs" @click.prevent="showAdministrationProductModal(product)">
                                            <i class="fa fa-pen mr-1"/>
                                            <span>Modifier le produit</span>
                                        </button>
                                    </div>
                                    <div class="prepress-product-statuses">
                                        <span class="badge mr-1 mb-1"
                                              :class="prepressStatusClass(getProductFileStatus(product, 'corpus'))">
                                            <span v-html="$t('ProductFilesStatusBadge.corpus')"/>
                                            :
                                            <span v-html="$t(`ProductFilesStatusBadge.${prepressStatusLabelKey(getProductFileStatus(product, 'corpus'))}`)"/>
                                        </span>
                                        <span class="badge mr-1 mb-1"
                                              :class="prepressStatusClass(getProductFileStatus(product, 'cover'))">
                                            <span v-html="$t('ProductFilesStatusBadge.cover')"/>
                                            :
                                            <span v-html="$t(`ProductFilesStatusBadge.${prepressStatusLabelKey(getProductFileStatus(product, 'cover'))}`)"/>
                                        </span>
                                        <span v-if="isWaitingForPhysicalPrintingApproval(product)" class="badge badge-warning mr-1 mb-1">
                                            <span v-html="$t('AdministrationOrders.waitingForClientBAT')"/>
                                        </span>
                                        <span v-else-if="isWaitingForClientConfirmation(product)" class="badge badge-warning mr-1 mb-1">
                                            <span v-html="$t('AdministrationOrders.waitingForClientConfirmation')"/>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="badge badge-success p-2">
                                <i class="fa fa-check-circle mr-1"/>
                                <span v-html="$t('AdministrationOrders.prepressReady')"/>
                            </div>
                        </div>
                        <div slot="actions" slot-scope="props">
                            <button v-if="props.row.type === 'order'"
                                    class="btn btn-primary btn-xs btn-block" @click.prevent="showProfileOrdersDetailsModal(props.row)">
                                <i class="fa fa-eye mr-2"/>
                                <span v-html="$t('AdministrationOrders.see')"/>
                            </button>
                            <button v-if="props.row.type === 'order'"
                                    class="btn btn-warning btn-xs btn-block" @click.prevent="showOrderModal(props.row)">
                                <i class="fa fa-pen mr-2"/>
                                <span v-html="$t('AdministrationOrders.update')"/>
                            </button>
                            <DownloadBilling :order="props.row" classes="btn btn-danger btn-xs btn-block"/>
                        </div>
                    </v-client-table>
                </div>
            </div>
        </transition>
        <ProfileOrdersDetailsModal ref="profileOrdersDetailsModal"/>
        <AdministrationOrderModal ref="administrationOrderModal" @order-updated="updateOrder"/>
        <AdministrationProductModal ref="administrationProductModal" @product-updated="updateProduct"/>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import FileSaver from "file-saver";
import APILoading from "@/components/shared/Misc/APILoading";
import { properDate } from "@/helpers/date";
import Order from "@/classes/Order";
import Product from "@/classes/Product";
import OrderPaymentStatusLabel from "@/components/shared/Order/OrderPaymentStatusLabel";
import OrderProductionStatusLabel from "@/components/shared/Order/OrderProductionStatusLabel";
import DownloadBilling from "@/components/shared/Order/DownloadBilling";
import ProfileOrdersDetailsModal from "@/components/Hermes/Profile/ProfileOrders/ProfileOrdersDetailsModal";
import User from "@/classes/User";
import AdministrationOrderModal from "@/components/Administration/AdministrationOrders/AdministrationOrderModal";
import SubmitBtn from "@/components/shared/Misc/SubmitBtn";
import { normalizeDecimal } from "@/helpers/price";
import AdministrationProductModal from "@/components/Administration/AdministrationProducts/AdministrationProductModal";

export default {
    name: "AdministrationOrders",
    components: {
        SubmitBtn,
        AdministrationProductModal,
        AdministrationOrderModal,
        ProfileOrdersDetailsModal,
        DownloadBilling, OrderProductionStatusLabel, OrderPaymentStatusLabel, APILoading,
    },
    data() {
        return {
            orders: null,
            isLoadingCSV: {
                order: false,
                quote: false,
            },
            showOnlyPrepressBlockedOrders: false,
            liveProductsById: {},
        };
    },
    filters: {
        normalizeDecimal,
    },
    computed: {
        ...mapGetters("currencies", {
            currencies: "currencies",
        }),
        ...mapGetters("config", {
            isAdministrationOrdersBlockageEnabled: "isAdministrationOrdersBlockageEnabled",
        }),
        columns() {
            const baseColumns = ["reference", "client", "createdAt", "sentAt", "status", "payment"];
            if (this.isAdministrationOrdersBlockageEnabled) {
                baseColumns.push("prepress");
            }
            baseColumns.push("actions");
            return baseColumns;
        },
        options() {
            const filterable = ["reference", "client", "createdAt", "status", "totalWT"];
            if (this.isAdministrationOrdersBlockageEnabled) {
                filterable.push("prepress");
            }
            return {
                headings: this.$t("AdministrationOrders.headings"),
                perPage: 10,
                sortable: ["reference", "createdAt", "status"],
                filterable,
                orderBy: {
                    ascending: false,
                    column: "createdAt",
                },
                filterAlgorithm: {
                    reference(row, query) {
                        query = query.toLowerCase();
                        const { proForma, bill, credit } = row.billing.reference;
                        return row.reference.toLowerCase().includes(query) ||
                            proForma && `PF${proForma}`.toLowerCase().includes(query) ||
                            bill && `FC${bill}`.toLowerCase().includes(query) ||
                            credit && `AV${credit}`.toLowerCase().includes(query);
                    },
                    client(row, query) {
                        query = query.toLowerCase();
                        const client = this.displayedOrderUser(row);
                        return client ? client.toLowerCase().includes(query) : undefined;
                    },
                    totalWT(row, query) {
                        const normalizedQuery = this.$options.filters.normalizeDecimal(query);
                        const normalizedTotalWT = this.$options.filters.normalizeDecimal(row.totalWT);
                        return normalizedTotalWT.includes(normalizedQuery);
                    },
                    prepress(row, query) {
                        if (!this.isAdministrationOrdersBlockageEnabled) {
                            return false;
                        }
                        const normalizedQuery = query.toLowerCase();
                        const prepressText = this.getOrderPrepressSearchText(row);
                        return prepressText.includes(normalizedQuery);
                    },
                },
            };
        },
        displayedOrders() {
            if (!this.isAdministrationOrdersBlockageEnabled || !this.showOnlyPrepressBlockedOrders) {
                return this.orders;
            }
            return this.orders.filter(order => this.hasBlockedProducts(order));
        },
        prepressBlockedOrdersCount() {
            if (!this.orders) {
                return 0;
            }
            return this.orders.filter(order => this.hasBlockedProducts(order)).length;
        },
        prepressBlockedFilterLabel() {
            if (this.showOnlyPrepressBlockedOrders) {
                return this.$t("AdministrationOrders.showAllOrders");
            }
            return this.$t("AdministrationOrders.showOnlyPrepressBlocked", { count: this.prepressBlockedOrdersCount });
        },
    },
    async created() {
        await this.getOrders();
    },
    methods: {
        async getOrders() {
            try {
                const query = { fields: "reference,user,createdAt,status,totalWT,currency,delivery,billing,type,products" };
                const { data: orders } = await this.$hermesAPI.getOrders(query);
                this.orders = orders.map(order => new Order(order));
                await this.refreshLiveProductsForOrders();
            } catch (err) {
                this.$error.displayError(err);
            }
        },
        displayedSendingDate(order) {
            return order.delivery.sendingDate ? this.properDate(order.delivery.sendingDate) : this.$t("AdministrationOrders.sendingDateNotDefined");
        },
        displayedOrderUser(order) {
            if (!order.user) {
                return this.$t("VueClientTable.undefined");
            }
            const client = new User(order.user);
            if (client.isProfessional) {
                return client.society.name ? client.society.name : this.$t("VueClientTable.undefined");
            }
            return client.fullName;
        },
        showProfileOrdersDetailsModal(order) {
            this.$refs.profileOrdersDetailsModal.show(order);
        },
        showOrderModal(order) {
            this.$refs.administrationOrderModal.show(order._id);
        },
        showAdministrationProductModal(product) {
            this.$refs.administrationProductModal.show(product);
        },
        updateOrder(order) {
            const idx = this.orders.findIndex(({ _id }) => _id === order._id);
            if (idx !== -1) {
                const updatedOrder = new Order(order);
                this.orders.splice(idx, 1, updatedOrder);
                this.refreshLiveProductsForOrders([updatedOrder]);
            }
        },
        updateProduct(product) {
            const nextProduct = new Product(product);
            this.liveProductsById = {
                ...this.liveProductsById,
                [String(nextProduct._id)]: nextProduct,
            };
        },
        async refreshLiveProductsForOrders(orders = this.orders) {
            if (!Array.isArray(orders) || !orders.length) {
                return;
            }
            const productIds = [...new Set(orders
                .filter(order => this.isOrderTrackedForPrepress(order))
                .flatMap(order => order.products || [])
                .map(product => product && product._id)
                .filter(Boolean)
                .map(id => String(id)))];
            if (!productIds.length) {
                return;
            }
            const chunks = [];
            const chunkSize = 10;
            for (let index = 0; index < productIds.length; index += chunkSize) {
                chunks.push(productIds.slice(index, index + chunkSize));
            }
            const nextLiveProductsById = { ...this.liveProductsById };
            for (const idsChunk of chunks) {
                const responses = await Promise.allSettled(idsChunk.map(id => this.$hermesAPI.getProduct(id)));
                responses.forEach((response, index) => {
                    if (response.status === "fulfilled" && response.value && response.value.data) {
                        const product = new Product(response.value.data);
                        nextLiveProductsById[String(idsChunk[index])] = product;
                    }
                });
            }
            this.liveProductsById = nextLiveProductsById;
        },
        togglePrepressBlockedOrders() {
            if (!this.isAdministrationOrdersBlockageEnabled) {
                return;
            }
            this.showOnlyPrepressBlockedOrders = !this.showOnlyPrepressBlockedOrders;
        },
        isOrderTrackedForPrepress(order) {
            const trackedProductionStatuses = ["pending", "preparing"];
            return !!(order && order.type === "order" && trackedProductionStatuses.includes(order.status.production));
        },
        getLiveProduct(product) {
            if (!product || !product._id) {
                return product;
            }
            const liveProduct = this.liveProductsById[String(product._id)];
            return liveProduct ? liveProduct : product;
        },
        getProductFileStatus(product, bookPart) {
            if (!product || !product.files || !product.files[bookPart]) {
                return undefined;
            }
            if (bookPart === "cover" &&
                (product.files.cover.status === "to-check" || product.files.cover.status === "checking") &&
                product.files.corpus.status !== "approved-by-client" && product.files.corpus.status !== "printable") {
                return "waiting-for-corpus";
            }
            return product.files[bookPart].status;
        },
        prepressStatusLabelKey(status) {
            const labelKeys = {
                "to-upload": "toUpload",
                "uploaded": "uploaded",
                "waiting-for-corpus": "waitingForCorpus",
                "to-check": "toCheck",
                "checking": "checking",
                "errored": "errored",
                "waiting-for-client-approval": "waitingForClientApproval",
                "fixing-by-operator": "fixingByOperator",
                "approved-by-client": "approvedByClient",
                "printable": "printable",
            };
            return labelKeys[status] ? labelKeys[status] : "toUpload";
        },
        prepressStatusClass(status) {
            const classes = {
                "to-upload": "badge-warning",
                "uploaded": "badge-info",
                "waiting-for-corpus": "badge-warning",
                "to-check": "badge-info",
                "checking": "badge-info",
                "errored": "badge-danger",
                "waiting-for-client-approval": "badge-warning",
                "fixing-by-operator": "badge-info",
                "approved-by-client": "badge-success",
                "printable": "badge-success",
            };
            return classes[status] ? classes[status] : "badge-secondary";
        },
        isProductPrepressReady(product) {
            const readyStatuses = ["approved-by-client", "printable"];
            const corpusStatus = this.getProductFileStatus(product, "corpus");
            const coverStatus = this.getProductFileStatus(product, "cover");
            return readyStatuses.includes(corpusStatus) && readyStatuses.includes(coverStatus) &&
                !this.isWaitingForPhysicalPrintingApproval(product);
        },
        hasPhysicalPrintingApprovalOption(product) {
            return !!(product && Array.isArray(product.options) && product.options.find(({ name }) => name === "physical-printing-approval"));
        },
        isWaitingForClientConfirmation(product) {
            const corpusStatus = this.getProductFileStatus(product, "corpus");
            const coverStatus = this.getProductFileStatus(product, "cover");
            return corpusStatus === "waiting-for-client-approval" || coverStatus === "waiting-for-client-approval";
        },
        isWaitingForClientUpload(product) {
            const corpusStatus = this.getProductFileStatus(product, "corpus");
            const coverStatus = this.getProductFileStatus(product, "cover");
            return corpusStatus === "to-upload" || coverStatus === "to-upload";
        },
        isWaitingForPhysicalPrintingApproval(product) {
            const corpusStatus = this.getProductFileStatus(product, "corpus");
            const coverStatus = this.getProductFileStatus(product, "cover");
            return corpusStatus === "printable" && coverStatus === "printable" &&
                this.hasPhysicalPrintingApprovalOption(product) && !product.files.printingApprovedByClient;
        },
        isProductWaitingForClient(product) {
            return this.isWaitingForClientUpload(product) ||
                this.isWaitingForClientConfirmation(product) ||
                this.isWaitingForPhysicalPrintingApproval(product);
        },
        isProductBlockedByPrepress(product) {
            const prepressStatuses = ["uploaded", "waiting-for-corpus", "to-check", "checking", "errored", "fixing-by-operator"];
            const corpusStatus = this.getProductFileStatus(product, "corpus");
            const coverStatus = this.getProductFileStatus(product, "cover");
            return prepressStatuses.includes(corpusStatus) || prepressStatuses.includes(coverStatus);
        },
        isPrepressTrackedProduct(product) {
            return !!(product && product.files && product.files.corpus && product.files.cover &&
                product.files.corpus.status && product.files.cover.status);
        },
        isProductPrepressBlocked(product) {
            return this.isPrepressTrackedProduct(product) && !this.isProductPrepressReady(product) &&
                !this.isProductWaitingForClient(product) && this.isProductBlockedByPrepress(product);
        },
        isProductClientPending(product) {
            return this.isPrepressTrackedProduct(product) && !this.isProductPrepressReady(product) &&
                this.isProductWaitingForClient(product);
        },
        getPrepressBlockedProducts(order) {
            if (!this.isOrderTrackedForPrepress(order) || !Array.isArray(order.products)) {
                return [];
            }
            return order.products
                .map(product => this.getLiveProduct(product))
                .filter(product => this.isProductPrepressBlocked(product));
        },
        getClientPendingProducts(order) {
            if (!this.isOrderTrackedForPrepress(order) || !Array.isArray(order.products)) {
                return [];
            }
            return order.products
                .map(product => this.getLiveProduct(product))
                .filter(product => this.isProductClientPending(product));
        },
        hasClientPendingProducts(order) {
            return this.getClientPendingProducts(order).length > 0;
        },
        hasPrepressBlockedProducts(order) {
            return this.getPrepressBlockedProducts(order).length > 0;
        },
        hasBlockedProducts(order) {
            return this.hasPrepressBlockedProducts(order) || this.hasClientPendingProducts(order);
        },
        getOrderPrepressSearchText(order) {
            if (!this.isOrderTrackedForPrepress(order)) {
                return "";
            }
            const blockedProducts = this.getPrepressBlockedProducts(order);
            const clientPendingProducts = this.getClientPendingProducts(order);
            if (!blockedProducts.length && !clientPendingProducts.length) {
                return this.$t("AdministrationOrders.prepressReady").toLowerCase();
            }
            return [...blockedProducts, ...clientPendingProducts].map(product => {
                const corpusStatus = this.$t(`ProductFilesStatusBadge.${this.prepressStatusLabelKey(this.getProductFileStatus(product, "corpus"))}`);
                const coverStatus = this.$t(`ProductFilesStatusBadge.${this.prepressStatusLabelKey(this.getProductFileStatus(product, "cover"))}`);
                return `${product.title || ""} ${corpusStatus} ${coverStatus}`;
            }).join(" ").toLowerCase();
        },
        async downloadCSV(type) {
            try {
                this.isLoadingCSV[type] = true;
                const response = await this.$hermesAPI.getOrders({ "response-format": "CSV", type });
                const blob = new Blob([response.data], { type: "text/csv" });
                FileSaver.saveAs(blob, `${type}s.csv`);
            } catch (err) {
                this.$error.displayError(err);
            } finally {
                this.isLoadingCSV[type] = false;
            }
        },
        properDate,
    },
};
</script>

<style scoped>
.prepress-cell {
    min-width: 300px;
}

.prepress-product {
    margin-bottom: 8px;
}

.prepress-product:last-child {
    margin-bottom: 0;
}

.prepress-product-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.prepress-product-statuses {
    margin-top: 4px;
}

.prepress-product-link {
    text-decoration: underline;
}
</style>